import { DateTime24, TimeInput24, hhmm, hourOf } from "./DateTime24";
import { useEffect, useState } from "react";
import { Car, Calculator, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import MoneyField from "./MoneyField";
import { useAuth } from "../lib/auth";
import { fetchAaTowPricing, updateAaTowPricing, quoteAaTow } from "../lib/api";
import { tenantWeekday, toTenantWall, wallToIso } from "../lib/tenantTime";

/**
 * AA Tow Pricing card — Integrations page.
 *
 * Top half: editable rate config (admin only).
 * Bottom half: interactive quote calculator (any staff role).
 *
 * After-hours surcharge is auto-detected from the chosen NZ datetime, but a
 * "Override: public holiday" checkbox lets the dispatcher force it when an
 * unscheduled day (e.g. company stand-down) is in effect.
 */

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AaTowPricingCard() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const [distance, setDistance] = useState(15);
  const [when, setWhen] = useState(() => toTenantWall());
  const [holidayOverride, setHolidayOverride] = useState(null); // null=auto, true=force, false=force-no
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    fetchAaTowPricing()
      .then(setCfg)
      .catch(() => toast.error("Failed to load AA Tow pricing"));
  }, []);

  // Auto-quote whenever inputs change (debounced via the state itself).
  useEffect(() => {
    if (!cfg || distance === "" || distance == null) return;
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const iso = wallToIso(when);
        const body = { distance_km: Number(distance), at: iso };
        if (holidayOverride !== null) body.is_public_holiday = holidayOverride;
        setQuote(await quoteAaTow(body));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Quote failed");
      } finally {
        setQuoting(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [distance, when, holidayOverride, cfg]);

  const saveRates = async () => {
    setSaving(true);
    try {
      const patch = {
        base_tow_rate: Number(cfg.base_tow_rate),
        free_km: Number(cfg.free_km),
        per_km_rate: Number(cfg.per_km_rate),
        after_hours_surcharge: Number(cfg.after_hours_surcharge),
        business_hours_start_hour: Number(cfg.business_hours_start_hour),
        business_hours_end_hour: Number(cfg.business_hours_end_hour),
      };
      setCfg(await updateAaTowPricing(patch));
      toast.success("AA Tow rates saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6">
        <p className="text-sm text-slate-500">Loading AA Tow pricing…</p>
      </section>
    );
  }

  return (
    <section
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6 space-y-5"
      data-testid="aa-tow-pricing-card"
    >
      <div className="flex items-center gap-2">
        <Car className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">
          AA Tow Pricing
        </h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-3">
        Rates supplied by AA Roadservice (ex-GST). Normal pricing applies Mon–Fri 8 am – 5 pm NZ time. Outside those hours, on weekends, or on NZ public holidays adds the after-hours surcharge.
      </p>

      {/* Editable rate config */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <MoneyField
          label="Base tow rate ($)"
          hint="Fixed cost up to & incl. the free-km threshold."
          value={cfg.base_tow_rate}
          onChange={(v) => setCfg({ ...cfg, base_tow_rate: v })}
          disabled={!isAdmin}
          testid="aa-base-rate"
        />
        <div>
          <Label className="uppercase-label">Free km (incl. in base)</Label>
          <Input
            type="number" step="1" min="0"
            value={cfg.free_km}
            onChange={(e) => setCfg({ ...cfg, free_km: e.target.value })}
            disabled={!isAdmin}
            data-testid="aa-free-km"
          />
        </div>
        <MoneyField
          label="Per-km rate beyond free ($)"
          value={cfg.per_km_rate}
          onChange={(v) => setCfg({ ...cfg, per_km_rate: v })}
          disabled={!isAdmin}
          testid="aa-per-km-rate"
        />
        <MoneyField
          label="After-hours surcharge ($)"
          value={cfg.after_hours_surcharge}
          onChange={(v) => setCfg({ ...cfg, after_hours_surcharge: v })}
          disabled={!isAdmin}
          testid="aa-ah-surcharge"
        />
        <div>
          <Label className="uppercase-label">Business hours start</Label>
          <TimeInput24
            value={hhmm(cfg.business_hours_start_hour)}
            onChange={(v) => setCfg({ ...cfg, business_hours_start_hour: hourOf(v) })}
            disabled={!isAdmin}
            dataTestId="aa-bh-start"
          />
          <p className="text-[11px] text-slate-400 mt-1">24h, NZ time. Default 08:00.</p>
        </div>
        <div>
          <Label className="uppercase-label">Business hours end</Label>
          <TimeInput24
            value={hhmm(cfg.business_hours_end_hour)}
            onChange={(v) => setCfg({ ...cfg, business_hours_end_hour: hourOf(v) })}
            disabled={!isAdmin}
            dataTestId="aa-bh-end"
          />
          <p className="text-[11px] text-slate-400 mt-1">24h, NZ time. Default 17:00.</p>
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={saveRates} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="aa-save-rates">
            <Save className="h-4 w-4.5" />
            {saving ? "Saving…" : "Save Rates"}
          </Button>
        </div>
      )}

      {/* Quote calculator */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-5 space-y-4">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <h3 className="font-display text-base font-black text-slate-800 dark:text-slate-100">
            Live Quote
          </h3>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label className="uppercase-label">Distance (km)</Label>
            <Input
              type="number" step="0.1" min="0"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              data-testid="aa-quote-distance"
            />
          </div>
          <div>
            <Label className="uppercase-label">When (NZ time, 24h)</Label>
            <DateTime24
              value={when}
              onChange={setWhen}
              dataTestId="aa-quote-when"
            />
          </div>
          <div>
            <Label className="uppercase-label">Holiday override</Label>
            <select
              className="w-full h-10 rounded-md border border-slate-200 dark:border-slate-700 dark:bg-slate-800 px-3 text-sm"
              value={holidayOverride === null ? "auto" : holidayOverride ? "yes" : "no"}
              onChange={(e) => {
                const v = e.target.value;
                setHolidayOverride(v === "auto" ? null : v === "yes");
              }}
              data-testid="aa-quote-holiday-override"
            >
              <option value="auto">Auto-detect from date</option>
              <option value="yes">Force: public holiday</option>
              <option value="no">Force: normal day</option>
            </select>
          </div>
        </div>

        {quote && (
          <div className="rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4 space-y-2" data-testid="aa-quote-result">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-300">Base tow ({quote.breakdown.free_km} km incl.)</span>
              <span className="font-semibold">{fmt(quote.breakdown.base_tow)}</span>
            </div>
            {quote.breakdown.extra_km > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-300">
                  Extra distance · {quote.breakdown.extra_km} km × {fmt(quote.breakdown.per_km_rate)}
                </span>
                <span className="font-semibold">{fmt(quote.breakdown.extra_km_charge)}</span>
              </div>
            )}
            {quote.breakdown.after_hours_surcharge > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-300">
                  {quote.is_public_holiday
                    ? `Public holiday surcharge · ${quote.weekday_name}`
                    : [0, 6].includes(tenantWeekday(quote.at))
                      ? `Weekend surcharge · ${quote.weekday_name}`
                      : "After-hours surcharge · outside 8 am – 5 pm"}
                </span>
                <span className="font-semibold">{fmt(quote.breakdown.after_hours_surcharge)}</span>
              </div>
            )}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between">
              <span className="font-display font-black text-slate-800 dark:text-slate-100">Total</span>
              <span className="font-display text-xl font-black text-emerald-700 dark:text-emerald-300" data-testid="aa-quote-total">
                {fmt(quote.total)}
              </span>
            </div>
            {quoting && <p className="text-[11px] text-slate-400">recalculating…</p>}
          </div>
        )}
      </div>
    </section>
  );
}
